import { Placeholder, SitecoreContextValue } from '@sitecore-jss/sitecore-jss-nextjs';
import useHelpers from 'hooks/helpers';
import { fundCentreActions } from '@/store/fundCentre';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useI18n } from 'next-localization';
import { getActiveTabKey, getAllFundsCount } from '@/store/fundCentre/selectors';
import {
  ALL_FUNDS_TAB_KEYS,
  ALL_FUNDS_TAB_TITLE_KEYS,
  TAB_TITLE_KEYS,
} from 'constants/fundCentreTabs';
import FundsListing from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsListing';
import styles from '../styles/FundCentreMainTabs.module.scss';
import classNames from 'classnames';
import { ComponentWithContextProps } from 'lib/component-props';

type TabTypeValue = {
  tabType: number;
};

type FieldsType = {
  fundCentreTabs: {
    value: TabTypeValue[];
  };
  fundCentreAllFundsTabs: {
    value: TabTypeValue[];
  };
  fundDetailsPageUrl: string;
  filterMappings: FiltersType;
};

type FundCentreProps = ComponentWithContextProps & {
  fields: FieldsType;
  context: SitecoreContextValue;
};

type FiltersType = {
  value: {
    filters: Array<{
      displayName: string;
      id: string;
      options: Array<{
        id: string;
        name: string;
      }>;
    }>;
  };
};

type TabItem = {
  key: string;
  title: string;
};

const TAB_KEYS: Record<'ALL_FUNDS' | 'FUNDS_IN_FOCUS' | 'MY_FUNDS', number> = {
  ALL_FUNDS: 0,
  FUNDS_IN_FOCUS: 1,
  MY_FUNDS: 2,
};

const FundCentreMainTabs = ({ rendering, fields, context }: FundCentreProps): JSX.Element => {
  const { anchorId, updateUrlParams } = useHelpers(rendering);
  const dispatch = useDispatch();
  const activeTabKey: number = useSelector(getActiveTabKey);
  const allFundsCount: number = useSelector(getAllFundsCount);
  const router = useRouter();
  const { t } = useI18n();

  const countryCode = context.fundCentreConfiguration?.countryInvestors.countryCode;
  const jurisdiction = context.fundCentreConfiguration?.countryInvestors.jurisdiction;
  const investorType = context.fundCentreConfiguration?.countryInvestors.investorType;
  const literatureAuthorization =
    context.fundCentreConfiguration?.countryInvestors.literatureAuthorization;
  const site: string = context.site?.name || '';
  const itemsPerPage: number = context.fundCentreConfiguration?.itemsPerPage || 20;
  const filters = fields.filterMappings?.value?.filters.map((filter) => ({
    ...filter,
    displayName: t(filter.displayNameKey),
  }));
  const showFundsInFocus: boolean = context.pageEditing
    ? true
    : activeTabKey === TAB_KEYS.FUNDS_IN_FOCUS;

  const allFundsTabTabs = useMemo<TabItem[]>(() => {
    return Object.entries(fields.fundCentreAllFundsTabs.value).map(([, value]) => ({
      key: ALL_FUNDS_TAB_KEYS[value.tabType],
      title: ALL_FUNDS_TAB_TITLE_KEYS[value.tabType],
    }));
  }, [fields.fundCentreAllFundsTabs.value]);

  useEffect(() => {
    dispatch(fundCentreActions.setCurrentPage(1));
    updateUrlParams([
      {
        name: 'page',
        value: 1,
      },
    ]);

    const { activeTab } = router.query;
    const tabNumber = activeTab ? +activeTab : 0;

    dispatch(fundCentreActions.setActiveTab(tabNumber));

    dispatch(fundCentreActions.setFundDetailsPageUrl(fields.fundDetailsPageUrl));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTabKey,
    fields.fundCentreAllFundsTabs,
    fields.fundDetailsPageUrl,
    itemsPerPage,
    router.query,
  ]);

  const toggleTab = useCallback(
    (tabKey: number) => {
      if (tabKey !== activeTabKey) {
        if (!context.pageEditing) {
          const searchParams = new URLSearchParams(window.location.search);
          dispatch(
            fundCentreActions.setPaginationData({
              take: 20,
              skip: 0,
            })
          );

          if (tabKey === TAB_KEYS.ALL_FUNDS) {
            searchParams.delete('activeTab');
            dispatch(fundCentreActions.setFilters([]));
          } else {
            dispatch(fundCentreActions.setSearchByISIN(false));
            searchParams.set('activeTab', String(tabKey));
            searchParams.delete('search');
          }

          searchParams.set('table', 'overview');

          const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
          router.replace(newUrl);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTabKey, dispatch, context.pageEditing]
  );

  const getTabTitle = useCallback(
    (tabKey: number) => {
      switch (tabKey) {
        case TAB_KEYS.ALL_FUNDS: {
          return t(TAB_TITLE_KEYS[tabKey]).replace('{token.fundsCount}', `${allFundsCount}`);
        }
        case TAB_KEYS.FUNDS_IN_FOCUS: {
          return t(TAB_TITLE_KEYS[tabKey]);
        }
        case TAB_KEYS.MY_FUNDS: {
          // TODO: Get funds count from API
          const fundsCount = 0;
          return t(TAB_TITLE_KEYS[tabKey]).replace('{token.fundsCount}', `${fundsCount}`);
        }
        default:
          return undefined;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allFundsCount]
  );

  return (
    <section id={anchorId} data-e2e-component-uid={rendering.uid}>
      <div className="background-primary">
        <div className="background-to-change">
          <div className="container">
            <div className="d-flex align-items-center gap-1 gap-md-4" data-testid="fund-tabs">
              {fields.fundCentreTabs.value.map((tab, index) => (
                <button
                  onClick={() => toggleTab(tab.tabType)}
                  key={index}
                  className={classNames(styles['tab-button'], {
                    [styles['tab-button--active']]: tab.tabType === activeTabKey,
                  })}
                  data-testid={`fund-tab-${index}`}
                >
                  <span className="d-flex align-items-center gap-2">
                    {tab.tabType === TAB_KEYS.MY_FUNDS && (
                      <em className="bi bi-bookmarks-fill d-none d-md-block" />
                    )}
                    <span className="subheader-1" data-testid="tab-title">
                      {getTabTitle(tab.tabType)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {activeTabKey === TAB_KEYS.ALL_FUNDS && (
        <>
          <FundsListing
            countryCode={countryCode ?? ''}
            investorType={investorType ?? ''}
            itemsPerPage={itemsPerPage}
            jurisdiction={jurisdiction ?? ''}
            literatureAuthorization={literatureAuthorization ?? ''}
            site={site}
            filters={filters}
            allFundsTabTabs={allFundsTabTabs}
          />
        </>
      )}

      {showFundsInFocus && (
        <Placeholder
          rendering={rendering}
          name="abrdn-strategies-in-focus"
          allFundsTabTabs={allFundsTabTabs}
          data-testid="strategies-in-focus-placeholder"
        />
      )}
    </section>
  );
};

export default FundCentreMainTabs;
